import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type Contract = {
  id: number;
  status: 'pending' | 'signed' | 'completed';
  created_at: string;
};

type Inquiry = {
  id: number;
  status: string;
  createdAt: string;
};

type ApiEnvelope<T> = {
  ok: boolean;
  data?: T;
  message?: string;
};

function isWithinDays(value: string, days: number): boolean {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const diffMs = Date.now() - date.getTime();
  return diffMs >= 0 && diffMs <= days * 24 * 60 * 60 * 1000;
}

export default function Dashboard() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      axios.get<ApiEnvelope<Contract[]>>(`${MainURL}/api/admin/contracts/list`),
      axios.get<ApiEnvelope<Inquiry[]>>(`${MainURL}/inquiry`, {
        params: { requesterId: getAdminSession()?.id },
      }),
    ]).then(([contractRes, inquiryRes]) => {
      if (cancelled) return;
      if (contractRes.status === 'fulfilled' && contractRes.value.data?.ok) {
        setContracts(contractRes.value.data.data || []);
      }
      if (inquiryRes.status === 'fulfilled' && inquiryRes.value.data?.ok) {
        setInquiries(inquiryRes.value.data.data || []);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // 2026-09-18부터 online_contracts는 실제 전자서명(계약 체결) 시점에만 생성되므로, 그 이전
  // 단계(신청~MVP확인)는 inquiries 쪽 미전환(status !== 'converted') row로 집계해 더해준다.
  const pendingInquiries = inquiries.filter((i) => i.status !== 'converted').length;
  const totalChurches = contracts.length + pendingInquiries;
  const signedCount = contracts.filter((c) => c.status === 'signed').length;
  const pendingCount = contracts.filter((c) => c.status === 'pending').length + pendingInquiries;
  const newThisMonth = contracts.filter((c) => isWithinDays(c.created_at, 30)).length;
  const recentInquiries = inquiries.filter((i) => isWithinDays(i.createdAt, 7)).length;

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">대시보드</h2>
          <p className="admin-panel-desc">내부 운영진을 위한 전반적인 서비스 현황 요약입니다.</p>
        </div>
      </div>

      <div className="admin-dashboard-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-label">전체 계약 교회 수</div>
          <div className="admin-stat-value">{loading ? '-' : totalChurches}</div>
          <div className="admin-stat-sub">계약 완료 {signedCount} · 서명 대기 {pendingCount}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">최근 30일 신규 계약</div>
          <div className="admin-stat-value">{loading ? '-' : newThisMonth}</div>
          <div className="admin-stat-sub">신규 오픈 현황</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-label">최근 7일 신청</div>
          <div className="admin-stat-value">{loading ? '-' : recentInquiries}</div>
          <div className="admin-stat-sub">전체 신청 {inquiries.length}건</div>
        </div>
      </div>

      <div className="admin-dashboard-grid admin-dashboard-grid--placeholder">
        <div className="admin-stat-card admin-stat-card--placeholder">
          <div className="admin-stat-label">성도 수 · MAU/DAU</div>
          <div className="admin-state">🚧 준비중입니다.</div>
        </div>
        <div className="admin-stat-card admin-stat-card--placeholder">
          <div className="admin-stat-label">주보 푸시 발송 통계</div>
          <div className="admin-state">🚧 준비중입니다.</div>
        </div>
        <div className="admin-stat-card admin-stat-card--placeholder">
          <div className="admin-stat-label">스토어 심사 이슈 · 긴급 CS 알림</div>
          <div className="admin-state">🚧 준비중입니다.</div>
        </div>
      </div>
    </div>
  );
}
